import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminSearchRealmUsers,
  type RealmUserSearchResult,
} from '@frontend/api-clients/adminUserClient';
import { authClient } from '@frontend/api-clients/authClient';

const PAGE_SIZE = 50;

export interface RealmUsersDirectoryProps {
  /** Bump or change to force a reload (e.g. after create/invite). */
  refreshKey?: number;
  selectedUserId?: string | null;
  onSelect?: (user: RealmUserSearchResult | null) => void;
}

function matchesFilter(u: RealmUserSearchResult, q: string): boolean {
  if (!q) return true;
  const hay = [u.lastName, u.firstName, u.email, u.username]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

export default function RealmUsersDirectory({
  refreshKey = 0,
  selectedUserId = null,
  onSelect,
}: RealmUsersDirectoryProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [users, setUsers] = useState<RealmUserSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(id);
  }, [query]);

  const load = useCallback(async (q: string, first: number, append: boolean) => {
    setLoading(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) return;
      const rows = await adminSearchRealmUsers(token, q, PAGE_SIZE, first);
      setUsers((current) => (append ? [...current, ...rows] : rows));
      setHasMore(rows.length >= PAGE_SIZE);
    } catch (e) {
      if (!append) setUsers([]);
      setError(e instanceof Error ? e.message : 'Failed to load realm users');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(debouncedQuery, 0, false);
  }, [debouncedQuery, refreshKey, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => matchesFilter(u, q));
  }, [users, query]);

  return (
    <div className="admin-realm-users-directory">
      <div className="admin-orch-section-header">
        <h4 className="admin-orch-subtitle">Realm users</h4>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={loading}
          onClick={() => void load(debouncedQuery, 0, false)}
        >
          Refresh
        </button>
      </div>
      <label className="admin-orch-field" htmlFor="realm-users-filter">
        <span>Filter</span>
        <input
          id="realm-users-filter"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Last, first, email, or username"
          autoComplete="off"
        />
      </label>
      {error ? (
        <div className="settings-message settings-error" role="alert">
          {error}
        </div>
      ) : null}
      {loading && users.length === 0 ? (
        <p className="ecosystem-description">Loading users…</p>
      ) : filtered.length === 0 ? (
        <p className="ecosystem-description">No realm users found.</p>
      ) : (
        <div className="admin-orch-list" role="list" aria-label="Realm user accounts">
          <div className="admin-orch-list-header admin-orch-list-header-users" aria-hidden="true">
            <span>Username</span>
            <span>Last</span>
            <span>First</span>
            <span>Email</span>
          </div>
          {filtered.map((u) => {
            const selected = selectedUserId === u.userId;
            return (
              <button
                key={u.userId}
                type="button"
                role="listitem"
                className={`admin-orch-list-row${selected ? ' is-selected' : ''}${
                  u.enabled === false ? ' is-muted' : ''
                }`}
                onClick={() => onSelect?.(u)}
                aria-pressed={selected}
              >
                <span className="admin-orch-list-row-cols admin-orch-list-row-cols-users">
                  <span>{u.username || '—'}</span>
                  <span>{u.lastName || '—'}</span>
                  <span>{u.firstName || '—'}</span>
                  <span>{u.email || '—'}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {hasMore ? (
        <div className="admin-create-user-actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={() => void load(debouncedQuery, users.length, true)}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
